import {Injectable} from '@angular/core';
import {LicenseDetails, LicenseStatus, LicenseType} from '@uigy/generated/graphql';
import {LicenseDetailsWithUsage, LicenseManagerService} from '@uigy/modules/license/license-manager.service';
import {NagService} from '@uigy/shared/components/nag/services/nag.service';
import {LanguageService} from '@uigy/shared/services/language.service';
import {DateTimeService} from '@uigy/shared/services/date-time/date-time.service';
import Utils from '@uigy/utils';
import {Subject, Subscription} from 'rxjs';
import {distinct, distinctUntilChanged, filter, map, takeUntil} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class LicenseNotificationsService {
  private destroy$ = new Subject<void>();

  constructor(
    private languageService: LanguageService,
    private licenseManagerService: LicenseManagerService,
    private dateTimeService: DateTimeService,
    private nagService: NagService,
  ) {
  }

  public start(): void {
    this.stop();
    this.messageSubscriptionExpiring();
    this.messageSubscriptionExpired();
    this.messageMaintenanceExpiring();
    this.messageMaintenanceExpired();
    this.messageAssetsExceeded();
  }

  public stop(): void {
    this.destroy$.next();
  }

  private messageSubscriptionExpiring(): Subscription {
    return this.licenseManagerService.getLicenseDetails()
      .pipe(
        Utils.filterNullsOperator(),
        filter(licenseDetails => licenseDetails.licenseType === LicenseType.Subscription),
        map(licenseDetails => {
          return DateTimeService.utcToLocal(licenseDetails.expirationDate).diff(this.dateTimeService.now(), 'days', true);
        }),
        filter(timeLeft => timeLeft <= 30 && timeLeft > 0),
        distinct(timeLeft => Math.ceil(timeLeft)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: timeLeft => {
          this.nagService.setMessage({
            text: this.languageService.getTranslationTyped('license_messages.subscription_expiring', {days: Math.ceil(timeLeft)}),
            dismissible: timeLeft > 10,
            type: 'license-expiring',
          });
        }, complete: () => {
          this.nagService.removeMessage('license-expiring');
        }
      });
  }

  private messageSubscriptionExpired(): Subscription {
    return this.licenseManagerService.getLicenseStatus()
      .pipe(
        distinctUntilChanged(),
        filter(status => status === LicenseStatus.SubscriptionExpired),
        takeUntil(this.destroy$)
      )
      .subscribe({next: () => {
        this.nagService.setMessage({
          text: this.languageService.texts.constant_nags.license_expired,
          type: 'license-expired',
        });
      }, complete: () => {
        this.nagService.removeMessage('license-expired');
      }});
  }

  private messageMaintenanceExpiring(): Subscription {
    return this.licenseManagerService.getLicenseDetails()
      .pipe(
        Utils.filterNullsOperator(),
        filter(licenseDetails => licenseDetails.licenseType === LicenseType.Perpetual),
        map(licenseDetails => {
          return DateTimeService.utcToLocal(licenseDetails.expirationDate).diff(this.dateTimeService.now(), 'days', true);
        }),
        filter(timeLeft => timeLeft <= 30 && timeLeft > 0),
        distinct(timeLeft => Math.ceil(timeLeft)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: timeLeft => {
          this.nagService.setMessage({
            text: this.languageService.getTranslationTyped('license_messages.maintenance_expiring', {days: Math.ceil(timeLeft)}),
            type: 'maintenance-expiring',
            dismissible: true,
          });
        }, complete: () => {
          this.nagService.removeMessage('maintenance-expiring');
        }
      });
  }

  private messageMaintenanceExpired(): Subscription {
    return this.licenseManagerService.getLicenseStatus()
      .pipe(
        distinctUntilChanged(),
        filter(status => status === LicenseStatus.MaintenanceExpired),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.nagService.setMessage({
            text: this.languageService.texts.license_messages.maintenance_expired,
            type: 'maintenance-expired',
            dismissible: true
          });
        }, complete: () => {
          this.nagService.removeMessage('maintenance-expired');
        }
      });
  }

  private messageAssetsExceeded(): Subscription {
    return this.licenseManagerService.getLicenseDetails()
      .pipe(
        Utils.filterNullsOperator(),
        filter(licenseDetails => (licenseDetails as LicenseDetails).assetCountThreshold > 0 && (licenseDetails as LicenseDetailsWithUsage).assetsUsage >= 0.9),
        map(licenseDetails => Math.floor((licenseDetails as LicenseDetailsWithUsage).assetsUsage * 100)),
        takeUntil(this.destroy$)
      )
      .subscribe({next: percent => {
        this.nagService.setMessage({
          text: this.languageService.getTranslationTyped('constant_nags.license_assets_exceeded', {percent}),
          dismissible: percent < 100,
          type: 'license-exceeded',
        });
      }, complete: () => {
          this.nagService.removeMessage('license-exceeded');
      }});
  }
}
